import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { Icone, type NomeDoIcone } from '../icones';
import './menu-acoes.css';

export type ItemMenu = {
  rotulo: string;
  aoClicar?: () => void;
  /** Destrutivo: texto vermelho, sem fundo. */
  perigo?: boolean;
  /** Desabilitado exige motivo, como no botão. */
  desabilitado?: boolean;
  motivo?: string;
  /** Régua acima deste item: separa grupos de natureza diferente. */
  separadorAntes?: boolean;
  /** Ícone antes do rótulo, só de enfeite: o rótulo continua dizendo tudo. */
  icone?: NomeDoIcone;
};

export type MenuAcoesProps = {
  itens: ItemMenu[];
  /** O que o menu comanda: "Ações da conversa". */
  rotulo?: string;
};

/**
 * O painel vai para a CAMADA SUPERIOR do navegador, e não fica absoluto.
 *
 * ── O defeito que isto conserta ───────────────────────────────────────────
 *
 * Dentro de tabela, o menu abria recortado: metade dele desaparecia dentro da
 * caixa da tabela. `.tabela` tem `overflow-x: auto` para rolar na horizontal, e
 * QUALQUER overflow diferente de `visible` recorta descendente absoluto — não
 * só `hidden`, que é o que `tools/lint-css-recorte.mjs` vigiava.
 *
 * E aqui não dava para tirar o overflow: é ele que faz a tabela rolar.
 *
 * ── Por que `popover`, e não portal ──────────────────────────────────────
 *
 * As duas tiram o painel do recorte. A diferença é que o `popover` muda apenas
 * a camada de DESENHO — o elemento continua no mesmo lugar da árvore do DOM.
 * Com portal ele mudaria de pai, e aí o fechar-ao-clicar-fora (que pergunta
 * `raiz.contains(alvo)`), a ordem de foco e os eventos que sobem precisariam
 * de remendo cada um.
 *
 * Também resolve de graça o que `position: fixed` não resolveria: um ancestral
 * com `transform` vira bloco de contenção e volta a recortar até o fixo — e a
 * casca do aplicativo tem `transform` na gaveta do celular.
 *
 * ── A posição é calculada, porque ancoragem em CSS ainda é de um navegador ──
 *
 * `anchor-name`/`position-anchor` resolveria isto em duas linhas de CSS, e só
 * o Chromium as implementa. Então a conta é aqui: abaixo do botão, alinhado à
 * direita dele, virando para cima quando não há altura embaixo.
 *
 * ── Rolar FECHA, em vez de reposicionar ──────────────────────────────────
 *
 * O menu pertence a uma linha. Se a linha sai de vista, um painel que a
 * seguisse ficaria flutuando sobre outra — dizendo "mais ações de Marcia" em
 * cima da linha do João. Fechar é a leitura honesta do gesto.
 */
const MARGEM = 4;
const BEIRADA = 8;

/*
 * ── Onde o navegador sabe ancorar, quem posiciona é o CSS ─────────────────
 *
 * Na lista das automações o painel abria colado no canto de cima à esquerda
 * (25 e 26/10), e a conta em JavaScript não se deixou medir. Com
 * `anchor-name`/`position-area` (Chromium), o painel fica preso ao botão pelo
 * próprio navegador, embaixo e alinhado à direita dele, e vira para cima quando
 * não cabe (`flip-block`). A conta continua para quem não tem o recurso.
 */
const ANCORA_NO_CSS =
  typeof CSS !== 'undefined' && typeof CSS.supports === 'function'
    ? CSS.supports('anchor-name: --menu')
    : false;

export function MenuAcoes({ itens, rotulo = 'Mais ações' }: MenuAcoesProps) {
  const [aberto, definirAberto] = useState(false);
  const raiz = useRef<HTMLSpanElement>(null);
  const painel = useRef<HTMLDivElement>(null);
  /** O botão medido no clique: é quando ele está, com certeza, desenhado na tela. */
  const noClique = useRef<DOMRect | null>(null);
  const [posicao, definirPosicao] = useState<{ top: number; left: number } | null>(null);
  const ancora = `--menu-acoes-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

  /*
   * `useLayoutEffect`, e não `useEffect`: entre pintar o painel no canto e
   * movê-lo para o lugar existe um quadro, e nesse quadro ele aparece torto.
   */
  useLayoutEffect(() => {
    const caixa = painel.current;
    if (aberto && caixa && ANCORA_NO_CSS) {
      if (typeof caixa.showPopover === 'function' && !caixa.matches(':popover-open')) {
        try {
          caixa.showPopover();
        } catch {
          // Já aberto por outro caminho.
        }
      }
      return;
    }
    /*
     * A âncora medida agora, ou a do clique quando a de agora vem zerada.
     *
     * Na lista de automações o painel abria colado no canto de cima à esquerda
     * (25/10): o botão chegava aqui com tamanho zero, e a conta dava 8px e 4px.
     * Medido no clique, o botão está na tela; zerado depois, vale a medida dele.
     */
    const agora = raiz.current?.getBoundingClientRect();
    const ancora =
      agora && (agora.width > 0 || agora.height > 0) ? agora : (noClique.current ?? agora);
    if (!aberto || !caixa || !ancora) return;

    // A camada superior. Sem suporte, o painel segue visível como antes — pior
    // no recorte, e nunca ausente.
    if (typeof caixa.showPopover === 'function' && !caixa.matches(':popover-open')) {
      try {
        caixa.showPopover();
      } catch {
        // Já aberto por outro caminho: a medição abaixo continua valendo.
      }
    }

    const { width: largura, height: altura } = caixa.getBoundingClientRect();
    const cabeEmbaixo = ancora.bottom + MARGEM + altura <= window.innerHeight - BEIRADA;

    definirPosicao({
      top: cabeEmbaixo ? ancora.bottom + MARGEM : Math.max(BEIRADA, ancora.top - MARGEM - altura),
      left: Math.min(
        Math.max(BEIRADA, ancora.right - largura),
        Math.max(BEIRADA, window.innerWidth - BEIRADA - largura),
      ),
    });
  }, [aberto]);

  /*
   * Rolagem em QUALQUER ancestral, e não só na janela: quem rola é o embrulho
   * da tabela, e o evento dele não sobe — por isso a captura.
   */
  useEffect(() => {
    if (!aberto) return;
    const fechar = () => definirAberto(false);
    window.addEventListener('scroll', fechar, true);
    window.addEventListener('resize', fechar);
    return () => {
      window.removeEventListener('scroll', fechar, true);
      window.removeEventListener('resize', fechar);
    };
  }, [aberto]);

  // Reabrir recalcula: posição velha faria o painel piscar no lugar de antes.
  useEffect(() => {
    if (!aberto) definirPosicao(null);
  }, [aberto]);

  /*
   * Fecha no clique fora e no Esc.
   *
   * Sem o Esc, quem abriu por teclado precisa tabular por todos os itens para
   * sair — e o menu tem justamente as ações que a pessoa não queria.
   */
  useEffect(() => {
    if (!aberto) return;
    const foraDele = (evento: MouseEvent) => {
      if (!raiz.current?.contains(evento.target as Node)) definirAberto(false);
    };
    const noTeclado = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') definirAberto(false);
    };
    document.addEventListener('mousedown', foraDele);
    document.addEventListener('keydown', noTeclado);
    return () => {
      document.removeEventListener('mousedown', foraDele);
      document.removeEventListener('keydown', noTeclado);
    };
  }, [aberto]);

  return (
    <span className="menu-acoes" ref={raiz}>
      <button
        type="button"
        className="menu-acoes__abrir"
        aria-haspopup="menu"
        aria-expanded={aberto}
        aria-label={rotulo}
        title={rotulo}
        style={ANCORA_NO_CSS ? ({ anchorName: ancora } as CSSProperties) : undefined}
        onClick={(evento) => {
          noClique.current = evento.currentTarget.getBoundingClientRect();
          definirAberto((estava) => !estava);
        }}
      >
        <svg
          width={20}
          height={20}
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.7}
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M4.6 10h.01M10 10h.01M15.4 10h.01" />
        </svg>
      </button>
      {aberto ? (
        <div
          className="menu-acoes__painel"
          role="menu"
          ref={painel}
          popover="manual"
          /*
            Invisível até a conta terminar. Sem isto, o painel aparece no canto
            da tela por um quadro — que é o mesmo defeito de layout de antes,
            só mais rápido.
          */
          style={
            ANCORA_NO_CSS
              ? ({
                  positionAnchor: ancora,
                  positionArea: 'bottom span-left',
                  positionTryFallbacks: 'flip-block',
                  marginBlock: `${MARGEM}px`,
                } as CSSProperties)
              : posicao
                ? { top: `${posicao.top}px`, left: `${posicao.left}px` }
                : { top: 0, left: 0, visibility: 'hidden' }
          }
        >
          {itens.map((item) => (
            <span key={item.rotulo} style={{ display: 'contents' }}>
              {item.separadorAntes ? (
                <span className="menu-acoes__separador" aria-hidden="true" />
              ) : null}
              <button
                type="button"
                role="menuitem"
                className={
                  item.perigo ? 'menu-acoes__item menu-acoes__item--perigo' : 'menu-acoes__item'
                }
                disabled={item.desabilitado}
                title={item.motivo}
                onClick={() => {
                  definirAberto(false);
                  item.aoClicar?.();
                }}
              >
                {item.icone ? <Icone nome={item.icone} /> : null}
                {item.rotulo}
              </button>
            </span>
          ))}
        </div>
      ) : null}
    </span>
  );
}
